import { Box, Button, Link, Skeleton, Typography } from '@mui/material';
import * as React from 'react';
import { BoxShadow, Palette, Sizes } from '../@shared/palette';
import { FlexBoxColumn, FlexBoxInline, LinkSx, PrimaryBtnSx } from '../@shared/templates';
import { openModelModalAction } from '../sagas/modelSaga/modelSagaActions';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './Home.css'
import { TrainedModelProviderInstance } from '../@shared/services/TrainedModelProvider';
import { SchemaApiValue } from '../@shared/Models/Models';
import { getIsAuthorized } from '../reducers/main/mainReducer';


const Home = () => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [latestSchemas, setLatestSchemas] = React.useState<SchemaApiValue[]>([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(getIsAuthorized)

  React.useEffect(() => {
    setIsLoading(true);
    if (!user) {
      return;
    }

    TrainedModelProviderInstance.top().then((data) => {
      if (data.data.length === 0) {
        setIsLoading(false);
        return;
      }

      const schemas = [] as SchemaApiValue[];

      for (let i = 0; i < data.data.length; i++) {
        if (!schemas.find(s => s.id === data.data[i].schemaId)) {
          schemas.push(data.data[i].schema);
        }
        schemas.find(s => s.id === data.data[i].schemaId)?.models.push(data.data[i]);
      }
      setLatestSchemas(schemas);
      setIsLoading(false);
    });
  }, [user]);

  const onOpenDocs = React.useCallback(() => {
    navigate('/Documentation');
  }, [navigate]);

  const handleAddSchema = () => dispatch(openModelModalAction())

  if (isLoading) {
    return <Box sx={{
      width: '100%',
      padding: "20px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "space-evenly",
      height: "100%"
    }}>
      <Box sx={{ ...FlexBoxColumn, alignItems: "center" }}>
        <Skeleton variant="text" width={300} sx={{ fontSize: Sizes.pageTitle }} />
        <Skeleton variant="text" width={500} sx={{ fontSize: Sizes.textPrimary, marginBottom: "20px" }} />
        <Box sx={{ ...FlexBoxInline, gap: "10px", position: "relative" }}>
          <Skeleton variant="text" width={200} sx={{ fontSize: Sizes.pageTitle, marginBottom: "5px" }} />
          <Skeleton variant="text" width={200} sx={{ fontSize: Sizes.pageTitle, marginBottom: "5px" }} />
        </Box>
      </Box>
    </Box>
  }

  if (latestSchemas.length > 0) {
    return <Box sx={{
      width: '100%',
      padding: "20px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "space-evenly",
      height: "100%"
    }}>
      <Box sx={{ ...FlexBoxColumn, alignItems: "center", width: "100%" }}>
        <Typography sx={{ color: Palette.textColorPrimary, fontSize: Sizes.pageTitle, textAlign: "center" }}>Welcome to MLArtisan</Typography>
        <Typography sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textPrimary, marginBottom: "20px", textAlign: "center" }}>
          Crafting intelligent ML solutions tailored to your needs. Train, fine-tune, and deploy powerful models seamlessly, transforming data into innovation.
        </Typography>
        <Box sx={{ ...FlexBoxColumn, gap: "20px", width: "100%", maxWidth: "1000px" }}>
          <Typography sx={{ color: Palette.textColorPrimary, fontSize: Sizes.pageThirdTitle }}>Recent schemas/models:</Typography>
        </Box>
        <Box sx={{ ...FlexBoxColumn, gap: "20px", width: "100%", maxWidth: "1000px", background: Palette.backgroundColorPrimary, overflowX: "hidden", padding: "20px", borderRadius: "5px", boxShadow: BoxShadow, position: "relative" }}>

          {latestSchemas.map((schema) => <Box key={schema.id} sx={{ ...FlexBoxColumn, gap: "10px", width: "100%", position: "relative" }}>
            <span style={{
              width: "1px",
              height: "calc(100% - 20px)",
              background: "#ccc",
              position: "absolute",
              top: "12px"
            }} />

            <Box key={schema.id} sx={{ ...FlexBoxInline, gap: "10px", paddingLeft: "10px", position: "relative" }}>
              <span style={{
                width: "7px",
                height: "1px",
                background: "#ccc",
                position: "absolute",
                top: "12px",
                left: "0"
              }} />
              <Link sx={{ ...LinkSx, color: Palette.textColorPrimary }} component="button" onClick={() => navigate(`/${schema.name}`)}>{schema.name}</Link>
            </Box>

            {schema.models.map((model) => <Box key={model.id} sx={{ ...FlexBoxInline, gap: "10px", paddingLeft: "40px", position: "relative" }}>
              <span style={{
                width: "37px",
                height: "1px",
                background: "#ccc",
                position: "absolute",
                top: "12px",
                left: "0"
              }} />
              <Link sx={{ ...LinkSx, color: Palette.textColorPrimary }} component="button" onClick={() => navigate(`/${schema.name}/${model.name}`)}>{model.name}</Link>
            </Box>)}
            <Box sx={{ ...FlexBoxInline, gap: "10px", paddingLeft: "40px", position: "relative" }}>
              <span style={{
                width: "37px",
                height: "1px",
                background: "#ccc",
                position: "absolute",
                top: "12px",
                left: "0"
              }} />
              <Link sx={{ ...LinkSx, color: Palette.textColorSecondary }} component="button" onClick={() => navigate(`/${schema.name}?tab=models`)}>all models...</Link>
            </Box>
          </Box>
          )}

        </Box>
        <Box sx={{ ...FlexBoxInline, gap: "20px", marginTop: "20px" }}>
          <Button sx={PrimaryBtnSx} onClick={handleAddSchema} >Create new schema</Button>
          <Link sx={LinkSx} component="button" variant="body2" onClick={onOpenDocs}>API Documentation</Link>
        </Box>
      </Box>
    </Box>
  }

  return <Box sx={{
    width: '100%',
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-evenly",
    height: "100%"
  }}>
    <Box sx={{ ...FlexBoxColumn, alignItems: "center", width: "100%" }}>
      <Typography sx={{ color: Palette.textColorPrimary, fontSize: Sizes.pageTitle }}>Welcome to MLArtisan</Typography>
      <Typography sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textPrimary, marginBottom: "20px" }}>
        Crafting intelligent ML solutions tailored to your needs. Train, fine-tune, and deploy powerful models seamlessly, transforming data into innovation.
      </Typography>
      <Box sx={{ ...FlexBoxInline, gap: "20px" }}>
        <Button sx={PrimaryBtnSx} onClick={handleAddSchema} >Create first schema</Button>
        <Link sx={LinkSx} component="button" variant="body2" onClick={onOpenDocs}>API Documentation</Link>
      </Box>
    </Box>
  </Box>
}

export default Home;
